import type { ProgressState } from "../exam/exams.js";
import type { FollowAlongStats } from "../practice/FollowAlongEngine.js";
import { getClassById, getStage } from "./curriculum.js";

export interface AdaptiveRecommendation {
  classId: string;
  title: string;
  reason: string;
}

/**
 * Select a remedial/next class from actual performance evidence.
 * It never unlocks content and never mutates progress.
 */
export function recommendRevision(
  progress: ProgressState,
  stats: FollowAlongStats | null | undefined,
  currentClassId?: string | null,
): AdaptiveRecommendation | null {
  const candidates: Array<{ id: string; reason: string }> = [];
  const pedalTotal = (stats?.pedalHits ?? 0) + (stats?.pedalMisses ?? 0);
  const velocityTotal = (stats?.velocityHits ?? 0) + (stats?.velocityMisses ?? 0);

  if (
    progress.unlockedStage >= 8 &&
    pedalTotal > 0 &&
    (stats?.pedalMisses ?? 0) / pedalTotal > 0.2
  ) {
    candidates.push({ id: "class-8-8", reason: "Pedal changes need a cleaner release/re-catch." });
  }
  if (
    progress.unlockedStage >= 8 &&
    velocityTotal > 0 &&
    (stats?.velocityMisses ?? 0) / velocityTotal > 0.25
  ) {
    candidates.push({ id: "class-8-10", reason: "Dynamic targets were outside the written velocity band." });
  }
  if ((stats?.jitterMs ?? 0) > 110 || (stats?.early ?? 0) + (stats?.late ?? 0) > 4) {
    const current = currentClassId ? getClassById(currentClassId) : undefined;
    if (current) {
      candidates.push({ id: current.id, reason: "Repeat once slower to reduce timing jitter." });
    }
  }
  if ((stats?.accuracyPct ?? 100) < 85 && currentClassId) {
    candidates.push({ id: currentClassId, reason: "Pitch accuracy is below the 85% mastery target." });
  }

  const first = candidates.find((c) => {
    const target = getClassById(c.id);
    return target && target.stage <= progress.unlockedStage;
  });
  if (first) {
    const target = getClassById(first.id)!;
    return { classId: target.id, title: `${target.code} ${target.title}`, reason: first.reason };
  }

  // Otherwise continue with the first unfinished class in the highest unlocked stage.
  const stage = getStage(progress.unlockedStage);
  const next = stage?.classes.find((c) => !progress.completedClassIds.includes(c.id));
  return next
    ? {
        classId: next.id,
        title: `${next.code} ${next.title}`,
        reason: "Next unfinished class in your current stage.",
      }
    : null;
}
